import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import Seo from "../components/common/Seo";
import PageHeader from "../components/common/PageHeader";
import { Reveal } from "../components/common/Reveal";
import {
  serviceOptions, industryOptions, companySizeOptions, contactMethodOptions, standardAnswers,
} from "../lib/content";
import { submitLead } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const phoneRe = /^[0-9+()\-\s]{6,40}$/;

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name."),
  work_email: z.string().email("Enter a valid work email."),
  phone: z.string().regex(phoneRe, "Enter a valid phone number."),
  company_name: z.string().min(1, "Company name is required."),
  company_website: z.string().max(200).optional().or(z.literal("")),
  industry: z.string().optional().or(z.literal("")),
  company_size: z.string().optional().or(z.literal("")),
  primary_objective: z.string().max(2000).optional().or(z.literal("")),
  services_of_interest: z.array(z.string()).optional(),
  current_stack: z.string().max(400).optional().or(z.literal("")),
  preferred_contact: z.string().optional().or(z.literal("")),
  message: z.string().max(4000).optional().or(z.literal("")),
  consent: z.literal(true, { errorMap: () => ({ message: "Consent is required to submit." }) }),
  company_fax: z.string().optional(),
});

const inputCls =
  "w-full bg-kv-bg2 border border-kv-steel/30 px-4 py-3 font-body text-sm text-kv-off placeholder:text-kv-steel/70 focus:outline-none focus:border-kv-electric focus:ring-1 focus:ring-kv-electric transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.16em] text-kv-steel mb-2";
const errCls = "mt-1.5 font-body text-xs text-red-400";

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-kv-electric"> *</span>}</label>
      {children}
      {error && <p className={errCls} role="alert" data-testid="field-error">{error}</p>}
    </div>
  );
}

function StyledSelect({ value, onChange, options, placeholder = "Select…", testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="w-full bg-kv-bg2 border-kv-steel/30 rounded-none px-4 py-3 h-auto font-body text-sm text-kv-off data-[placeholder]:text-kv-steel/70 focus:border-kv-electric focus:ring-1 focus:ring-kv-electric"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-kv-bg2 border-kv-steel/30 text-kv-off rounded-none">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="text-kv-grey focus:bg-kv-royal/20 focus:text-kv-off cursor-pointer">
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();
  const estimate = location.state?.estimate || null;
  const {
    register, handleSubmit, watch, setValue, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      services_of_interest: estimate?.service ? [estimate.service] : [],
      message: estimate?.message || "",
      industry: "",
      company_size: "",
      preferred_contact: "",
      consent: false,
      company_fax: "",
    },
  });

  const selectedServices = watch("services_of_interest") || [];

  const toggleService = (name) => {
    const next = selectedServices.includes(name)
      ? selectedServices.filter((s) => s !== name)
      : [...selectedServices, name];
    setValue("services_of_interest", next, { shouldValidate: false });
  };

  const onSubmit = async (data) => {
    try {
      const res = await submitLead(data);
      if (res.status === "success") {
        setSubmitted(true);
        toast.success("Request received. We'll be in touch shortly.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        toast.error("This request was just submitted. Please wait a moment before retrying.");
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    }
  };

  if (submitted) {
    return (
      <>
        <Seo title="Thank you — Kratos Venture Group" description="Your strategy call request has been received." path="/contact" />
        <section className="relative min-h-[80vh] flex items-center kv-radial" data-testid="contact-success">
          <div className="absolute inset-0 kv-grid-bg opacity-25" aria-hidden />
          <div className="relative max-w-2xl mx-auto px-6 md:px-12 text-center">
            <CheckCircle2 className="h-14 w-14 text-kv-electric mx-auto" />
            <h1 className="mt-8 font-display text-4xl md:text-5xl font-medium tracking-tight text-kv-off">Request received.</h1>
            <p className="mt-6 font-body text-base md:text-lg text-kv-grey">
              Thank you. A member of the Kratos team will review your request and follow up to schedule your strategy session.
            </p>
            <div className="mt-10">
              <Link to="/" className="kv-link font-body text-kv-electric hover:text-kv-off" data-testid="success-home-link">Return home →</Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Book a Strategy Call — Kratos Venture Group"
        description="Start with a strategy session. We'll identify where better systems, automation, and intelligent agents can create the greatest impact."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Book a strategy call."
        intro="Tell us about your business. We'll identify where better systems, automation, and intelligent agents can create the greatest operational and commercial impact."
      />

      <section className="relative py-12 md:py-20" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            {estimate && (
              <div className="mb-8 border border-kv-electric/30 bg-kv-royal/10 px-5 py-4" data-testid="contact-estimate-banner">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-electric mb-1">Estimate attached</div>
                <p className="font-body text-sm text-kv-grey">
                  Your CRM estimate for <span className="text-kv-off font-medium">{estimate.users} user{estimate.users === 1 ? "" : "s"}</span> — approximately <span className="text-kv-off font-medium">${estimate.monthly.toLocaleString("en-US")}/mo</span> — has been added to your message below.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" data-testid="contact-form" noValidate>
              {/* Honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register("company_fax")} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full name" required error={errors.full_name?.message}>
                  <input className={inputCls} placeholder="Jane Doe" data-testid="input-full-name" {...register("full_name")} />
                </Field>
                <Field label="Work email" required error={errors.work_email?.message}>
                  <input className={inputCls} type="email" placeholder="jane@company.com" data-testid="input-work-email" {...register("work_email")} />
                </Field>
                <Field label="Phone" required error={errors.phone?.message}>
                  <input className={inputCls} placeholder="+1 555 000 0000" data-testid="input-phone" {...register("phone")} />
                </Field>
                <Field label="Company name" required error={errors.company_name?.message}>
                  <input className={inputCls} placeholder="Company Inc." data-testid="input-company-name" {...register("company_name")} />
                </Field>
                <Field label="Company website" error={errors.company_website?.message}>
                  <input className={inputCls} placeholder="company.com" data-testid="input-company-website" {...register("company_website")} />
                </Field>
                <Field label="Industry">
                  <StyledSelect value={watch("industry")} onChange={(v) => setValue("industry", v, { shouldValidate: false })} options={industryOptions} testId="select-industry" />
                </Field>
                <Field label="Company size">
                  <StyledSelect value={watch("company_size")} onChange={(v) => setValue("company_size", v, { shouldValidate: false })} options={companySizeOptions} testId="select-company-size" />
                </Field>
                <Field label="Preferred contact method">
                  <StyledSelect value={watch("preferred_contact")} onChange={(v) => setValue("preferred_contact", v, { shouldValidate: false })} options={contactMethodOptions} testId="select-contact-method" />
                </Field>
              </div>

              <Field label="Primary objective">
                <input className={inputCls} placeholder="What are you trying to achieve?" data-testid="input-objective" {...register("primary_objective")} />
              </Field>

              <div>
                <label className={labelCls}>Services of interest</label>
                <div className="flex flex-wrap gap-2" data-testid="services-checkboxes">
                  {serviceOptions.map((s) => {
                    const active = selectedServices.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        onClick={() => toggleService(s)}
                        data-testid={`service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        aria-pressed={active}
                        className={`font-body text-sm px-4 py-2 border transition-colors ${
                          active ? "border-kv-electric text-kv-off bg-kv-royal/15" : "border-kv-steel/30 text-kv-grey hover:border-kv-steel/60"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Current CRM / tech stack">
                <input className={inputCls} placeholder="e.g. HubSpot, Salesforce, custom…" data-testid="input-stack" {...register("current_stack")} />
              </Field>

              <Field label="Message">
                <textarea rows={5} className={inputCls} placeholder="Tell us a little about your operation." data-testid="input-message" {...register("message")} />
              </Field>

              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-kv-off" data-testid="input-consent" {...register("consent")} />
                  <span className="font-body text-sm text-kv-grey leading-relaxed">
                    I agree that Kratos Venture Group may contact me about my request and I have read the{" "}
                    <Link to="/privacy-policy" className="text-kv-electric hover:text-kv-off underline underline-offset-2">Privacy Policy</Link>.
                  </span>
                </label>
                {errors.consent && <p className={errCls} role="alert" data-testid="consent-error">{errors.consent.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-medium tracking-wide bg-kv-off text-kv-bg border border-kv-off hover:bg-kv-navy hover:shadow-[0_0_24px_rgba(0,0,0,0.12)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : "Book a Strategy Call"}
              </button>

              <p className="font-body text-xs text-kv-steel leading-relaxed max-w-xl">
                {standardAnswers.security}
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
